import { useEffect, useState } from 'react';
import { getSkill, type AdminSkill, type SkillField, type SkillDef } from '@/api';

type SkillPatch = Partial<
  Pick<SkillDef, 'name' | 'description' | 'body' | 'category' | 'icon' | 'inputs' | 'outputs' | 'concepts' | 'tags'>
> & { active?: boolean };

function csv(xs?: string[]) {
  return (xs || []).join(', ');
}

function parseCSV(raw: string): string[] {
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function fieldsFrom(rows: SkillField[]): SkillField[] {
  return rows.map((f) => ({ name: f.name, type: f.type }));
}

export default function SkillDrawer({
  skill,
  onClose,
  onSave,
  onDelete,
}: {
  skill: AdminSkill | null;
  onClose: () => void;
  onSave: (id: string, body: SkillPatch) => Promise<void>;
  onDelete: (s: AdminSkill) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState('');
  const [concepts, setConcepts] = useState('');
  const [body, setBody] = useState('');
  const [active, setActive] = useState(true);
  const [inputs, setInputs] = useState<SkillField[]>([]);
  const [outputs, setOutputs] = useState<SkillField[]>([]);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  useEffect(() => {
    if (!skill) return;
    setName(skill.name);
    setDescription(skill.description || '');
    setCategory(skill.category || '');
    setTags(csv(skill.tags));
    setConcepts(csv(skill.concepts));
    setActive(skill.active);
    setInputs(fieldsFrom(skill.inputs || []));
    setOutputs(fieldsFrom(skill.outputs || []));
    setBody('');
    setConfirmDelete(false);
    setLoadErr(null);
    getSkill(skill.id)
      .then((full) => {
        setBody(full.body || '');
        setName(full.name);
        setDescription(full.description || '');
        setCategory(full.category || '');
        setTags(csv(full.tags));
        setConcepts(csv(full.concepts));
        setActive(full.active ?? !full.inactive);
        setInputs(fieldsFrom(full.inputs || []));
        setOutputs(fieldsFrom(full.outputs || []));
      })
      .catch((e) => setLoadErr(e instanceof Error ? e.message : 'failed'));
  }, [skill]);

  if (!skill) return null;

  const updateField = (side: 'in' | 'out', i: number, key: keyof SkillField, value: string) => {
    const set = side === 'in' ? setInputs : setOutputs;
    const cur = side === 'in' ? inputs : outputs;
    set(cur.map((f, idx) => (idx === i ? { ...f, [key]: value } : f)));
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-lg h-full bg-surface border-l border-border overflow-y-auto">
        <div className="sticky top-0 bg-surface border-b border-border px-5 py-4 flex items-center justify-between">
          <h2 className="text-base font-semibold text-textp">Skill details</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-texts hover:text-textp hover:bg-surface2 transition-colors"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <form
          className="p-5 space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            if (busy) return;
            setBusy(true);
            try {
              await onSave(skill.id, {
                name,
                description,
                category,
                tags: parseCSV(tags),
                concepts: parseCSV(concepts),
                body,
                active,
                inputs,
                outputs,
              });
            } finally {
              setBusy(false);
            }
          }}
        >
          {loadErr && (
            <div className="text-sm text-error bg-error/10 border border-error/30 rounded-md px-3 py-2">{loadErr}</div>
          )}

          <label className="flex items-center justify-between gap-3 bg-bg border border-border rounded-lg px-3 py-2">
            <span className="text-sm text-textp">Active</span>
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          </label>

          <div>
            <label className="text-xs text-texts">Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full bg-bg border border-border rounded-lg px-3 py-2 text-sm text-textp"
            />
          </div>
          <div>
            <label className="text-xs text-texts">Id</label>
            <p className="mt-1 text-sm font-mono text-texts break-all">{skill.id}</p>
          </div>
          <div>
            <label className="text-xs text-texts">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="mt-1 w-full bg-bg border border-border rounded-lg px-3 py-2 text-sm text-textp"
            />
          </div>
          <div>
            <label className="text-xs text-texts">Category</label>
            <input
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="mt-1 w-full bg-bg border border-border rounded-lg px-3 py-2 text-sm text-textp"
            />
          </div>
          <div>
            <label className="text-xs text-texts">Tags</label>
            <input
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              className="mt-1 w-full bg-bg border border-border rounded-lg px-3 py-2 text-sm text-textp"
            />
          </div>
          <div>
            <label className="text-xs text-texts">Concepts</label>
            <input
              value={concepts}
              onChange={(e) => setConcepts(e.target.value)}
              className="mt-1 w-full bg-bg border border-border rounded-lg px-3 py-2 text-sm text-textp"
            />
          </div>

          {(['inputs', 'outputs'] as const).map((side) => {
            const rows = side === 'inputs' ? inputs : outputs;
            const set = side === 'inputs' ? setInputs : setOutputs;
            const key = side === 'inputs' ? 'in' : 'out';
            return (
              <div key={side}>
                <div className="flex items-center justify-between">
                  <label className="text-xs text-texts capitalize">{side}</label>
                  <button
                    type="button"
                    onClick={() => set([...rows, { name: '', type: '' }])}
                    className="text-xs text-accent hover:text-textp"
                  >
                    Add
                  </button>
                </div>
                <div className="mt-1 space-y-2">
                  {rows.map((f, i) => (
                    <div key={`${side}-${i}`} className="flex gap-2">
                      <input
                        value={f.name}
                        onChange={(e) => updateField(key, i, 'name', e.target.value)}
                        placeholder="name"
                        className="flex-1 bg-bg border border-border rounded-lg px-3 py-1.5 text-sm text-textp"
                      />
                      <input
                        value={f.type}
                        onChange={(e) => updateField(key, i, 'type', e.target.value)}
                        placeholder="type"
                        className="flex-1 bg-bg border border-border rounded-lg px-3 py-1.5 text-sm text-textp"
                      />
                      <button
                        type="button"
                        onClick={() => set(rows.filter((_, idx) => idx !== i))}
                        className="w-8 h-8 text-texts hover:text-error"
                      >
                        <i className="ri-close-line" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          <div>
            <label className="text-xs text-texts">Body</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={10}
              className="mt-1 w-full bg-bg border border-border rounded-lg px-3 py-2 text-sm font-mono text-textp"
            />
          </div>

          <div className="flex items-center justify-between gap-3 pt-2">
            {confirmDelete ? (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={async () => {
                    setBusy(true);
                    try {
                      await onDelete(skill);
                    } finally {
                      setBusy(false);
                    }
                  }}
                  className="text-sm px-3 py-1.5 rounded-lg bg-error text-white"
                >
                  Confirm delete
                </button>
                <button type="button" onClick={() => setConfirmDelete(false)} className="text-sm text-texts">
                  Cancel
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => setConfirmDelete(true)} className="text-sm text-error">
                Delete
              </button>
            )}
            <button
              type="submit"
              disabled={busy}
              className="text-sm px-3 py-1.5 rounded-lg bg-accent text-accent-text disabled:opacity-50"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
